import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Search, Trash2, ShieldCheck, ShieldAlert, Users as UsersIcon, Building2, MapPin, Lock, Clock, ArrowDown, ArrowUp, Briefcase, UserRound } from "lucide-react";

const ROLE_COLORS = {
  admin: "bg-[#ff000022] text-[color:var(--accent)]",
  manager: "bg-[#00e5ff20] text-[#00e5ff]",
  employee: "bg-[#242c3d] text-slate-300",
};

function relativeAgo(iso) {
  if (!iso) return { label: "Never", tone: "text-slate-600", online: false, stale: true };
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return { label: "Never", tone: "text-slate-600", online: false, stale: true };
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  const online = s < 60;
  if (online) return { label: "Online now", tone: "text-[color:var(--accent)]", online: true, stale: false };
  if (s < 3600) return { label: `${Math.floor(s / 60)}m ago`, tone: "text-slate-300", online: false, stale: false };
  if (s < 86400) return { label: `${Math.floor(s / 3600)}h ago`, tone: "text-slate-400", online: false, stale: false };
  const days = Math.floor(s / 86400);
  if (days < 30) return { label: `${days}d ago`, tone: days > 7 ? "text-[#ffb020]" : "text-slate-400", online: false, stale: days > 7 };
  const months = Math.floor(days / 30);
  return { label: `${months}mo ago`, tone: "text-[#ff3b5c]", online: false, stale: true };
}

export default function TeamManage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [presence, setPresence] = useState({});
  const [positions, setPositions] = useState([]);
  const [savingRow, setSavingRow] = useState(null); // "userId:field" while a save is in flight
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  // sort by last-seen — null = default (server order), "asc" = oldest/never first (inactive on top), "desc" = newest first
  const [sortLastSeen, setSortLastSeen] = useState(null);

  const load = useCallback(() => api.get("/users").then(({ data }) => setUsers(data)), []);
  useEffect(() => {
    load();
    api.get("/positions").then(({ data }) => setPositions(data || [])).catch(() => {});
    const loadPresence = () => api.get("/presence").then(({ data }) => {
      setPresence(Object.fromEntries((data || []).map((p) => [p.user_id, p])));
    }).catch((e) => console.debug("presence poll failed:", e?.message));
    loadPresence();
    const t = setInterval(loadPresence, 30000);
    return () => clearInterval(t);
  }, [load]);

  const positionById = useMemo(() => Object.fromEntries(positions.map((p) => [p.id, p])), [positions]);
  const eligibleManagers = useMemo(
    () => users.filter((u) => u.role === "admin" || u.role === "manager"),
    [users]
  );
  const userNameById = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u.name])), [users]);

  const isAdmin = user?.role === "admin";

  const reassignPosition = async (target, newPositionId) => {
    if ((target.position_id || "") === (newPositionId || "")) return;
    setSavingRow(`${target.id}:position`);
    try {
      await api.post(`/users/${target.id}/position`, { position_id: newPositionId || null });
      const label = newPositionId ? positionById[newPositionId]?.name || "position" : "no position";
      toast.success(`${target.name} → ${label}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reassign position");
    } finally {
      setSavingRow(null);
    }
  };

  const reassignManager = async (target, newManagerId) => {
    if ((target.manager_id || "") === (newManagerId || "")) return;
    if (newManagerId && newManagerId === target.id) {
      toast.error("A person can't manage themselves");
      return;
    }
    setSavingRow(`${target.id}:manager`);
    try {
      await api.patch(`/users/${target.id}`, { manager_id: newManagerId || null });
      const label = newManagerId ? userNameById[newManagerId] || "manager" : "no manager";
      toast.success(`${target.name} reports to ${label}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reassign manager");
    } finally {
      setSavingRow(null);
    }
  };

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    const base = users.filter((u) => {
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      if (!query) return true;
      return [u.name, u.email, u.department, u.location, u.discord_name].some((f) => (f || "").toLowerCase().includes(query));
    });
    if (!sortLastSeen) return base;
    const ts = (u) => {
      const raw = presence[u.id]?.last_seen;
      return raw ? new Date(raw).getTime() : 0; // "Never" / null → epoch 0 so they float to the top when asc
    };
    return [...base].sort((a, b) => (sortLastSeen === "asc" ? ts(a) - ts(b) : ts(b) - ts(a)));
  }, [users, q, roleFilter, sortLastSeen, presence]);

  const cycleLastSeenSort = () => setSortLastSeen((cur) => (cur === "asc" ? "desc" : cur === "desc" ? null : "asc"));

  const canDelete = (target) => {
    if (target.id === user.id) return false;
    if (target.role === "admin" && user.role !== "admin") return false;
    return true;
  };

  const toggleSelect = (id) => setSelected((cur) => {
    const next = new Set(cur);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const selectableIds = useMemo(() => filtered.filter(canDelete).map((u) => u.id), [filtered, user]);
  const allChecked = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const toggleAll = () => setSelected((cur) => (allChecked ? new Set() : new Set(selectableIds)));

  const undoRestore = async (docs) => {
    for (const doc of docs) await api.post("/users/restore", { doc }).catch(() => {});
    toast.success(`Restored ${docs.length}`);
    load();
  };

  const removeOne = async (target) => {
    if (!window.confirm(`Delete ${target.name}? Undo available for 6 seconds.`)) return;
    try {
      const { data } = await api.delete(`/users/${target.id}`);
      load();
      toast(`${target.name} deleted`, {
        duration: 6000,
        action: { label: "Undo", onClick: () => undoRestore([data.deleted]) },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} teammate${ids.length === 1 ? "" : "s"}? Undo available for 6s.`)) return;
    try {
      const { data } = await api.post("/users/bulk-delete", { ids });
      setSelected(new Set());
      load();
      const okCount = data.deleted?.length || 0;
      const errCount = data.errors?.length || 0;
      toast(`Deleted ${okCount}${errCount ? ` · ${errCount} skipped` : ""}`, {
        duration: 6000,
        action: { label: "Undo", onClick: () => undoRestore(data.deleted || []) },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    }
  };

  const counts = {
    admin: users.filter((u) => u.role === "admin").length,
    manager: users.filter((u) => u.role === "manager").length,
    employee: users.filter((u) => u.role === "employee").length,
  };

  return (
    <div className="space-y-6" data-testid="team-manage-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--accent)]">People Ops</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Team Management</h1>
          <p className="text-sm text-slate-400 mt-1">
            {users.length} teammates · {counts.admin} admin · {counts.manager} manager · {counts.employee} employee
          </p>
        </div>
        {selected.size > 0 && (
          <button
            onClick={bulkDelete}
            data-testid="team-bulk-delete"
            className="px-4 py-2.5 bg-[#ff3b5c] text-white rounded-lg font-bold text-sm inline-flex items-center gap-2 hover:opacity-90"
          >
            <Trash2 size={14} /> Delete {selected.size} selected
          </button>
        )}
      </div>

      <div className="card-surface p-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={14} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="team-search"
            placeholder="Search name, email, department, discord…"
            className="w-full pl-9 pr-3 py-2 text-sm bg-[#0b0d12] border border-[#2a3447] rounded-lg outline-none focus:border-[color:var(--accent)]"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          data-testid="team-role-filter"
          className="bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 py-2 text-sm outline-none"
        >
          <option value="all">All roles</option>
          <option value="admin">Admin</option>
          <option value="manager">Manager</option>
          <option value="employee">Employee</option>
        </select>
      </div>

      <div className="card-surface overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0b0d12] border-b border-[#2a3447] text-slate-400 uppercase text-xs tracking-widest">
              <tr>
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    ref={(el) => { if (el) el.indeterminate = !allChecked && selectableIds.some((id) => selected.has(id)); }}
                    onChange={toggleAll}
                    aria-label="Select all"
                    data-testid="team-check-all"
                    className="accent-[color:var(--accent)] cursor-pointer"
                  />
                </th>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Department</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Location</th>
                <th className="text-left px-4 py-3">Role</th>
                <th className="text-left px-4 py-3 hidden lg:table-cell min-w-[180px]">Position</th>
                <th className="text-left px-4 py-3 hidden lg:table-cell min-w-[180px]">Reports to</th>
                <th className="text-left px-4 py-3 hidden sm:table-cell">
                  <button
                    type="button"
                    onClick={cycleLastSeenSort}
                    data-testid="team-sort-lastseen"
                    title={sortLastSeen === "asc" ? "Sorted: inactive first · click to flip" : sortLastSeen === "desc" ? "Sorted: most-active first · click to clear" : "Click to sort inactive first"}
                    className={`inline-flex items-center gap-1 uppercase tracking-widest text-xs transition-colors ${sortLastSeen ? "text-[color:var(--accent)]" : "text-slate-400 hover:text-slate-200"}`}
                  >
                    Last seen
                    {sortLastSeen === "asc" && <ArrowUp size={11} />}
                    {sortLastSeen === "desc" && <ArrowDown size={11} />}
                  </button>
                </th>
                <th className="px-4 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const deletable = canDelete(u);
                return (
                  <tr key={u.id} data-testid={`team-row-${u.id}`} className={`border-b border-[#1a202c] hover:bg-[#121620] ${selected.has(u.id) ? "bg-[color:var(--accent)]/5" : ""}`}>
                    <td className="px-4 py-3">
                      {deletable ? (
                        <input
                          type="checkbox"
                          checked={selected.has(u.id)}
                          onChange={() => toggleSelect(u.id)}
                          data-testid={`team-check-${u.id}`}
                          aria-label={`Select ${u.name}`}
                          className="accent-[color:var(--accent)] cursor-pointer"
                        />
                      ) : (
                        <Lock size={12} className="text-slate-600" title="Protected — can't be selected for delete" />
                      )}
                    </td>
                    <td className="px-4 py-3 font-semibold flex items-center gap-2">
                      {u.avatar_url ? (
                        <img src={u.avatar_url} alt={u.name} className="w-7 h-7 rounded-full object-cover border border-[#2a3447]" />
                      ) : (
                        <span className="w-7 h-7 rounded-full bg-[#242c3d] flex items-center justify-center text-xs font-bold text-[color:var(--accent)] border border-[#2a3447]">
                          {(u.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                        </span>
                      )}
                      <span className="truncate">{u.name}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-400 truncate">{u.email}</td>
                    <td className="px-4 py-3 text-slate-300 hidden md:table-cell">{u.department || "—"}</td>
                    <td className="px-4 py-3 text-slate-300 hidden md:table-cell">{u.location || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`pill ${ROLE_COLORS[u.role] || ROLE_COLORS.employee}`}>{u.role}</span>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell" data-testid={`team-position-cell-${u.id}`}>
                      {isAdmin ? (
                        <div className="flex items-center gap-1.5">
                          <Briefcase size={12} className="text-slate-500 shrink-0" />
                          <select
                            value={u.position_id || ""}
                            onChange={(e) => reassignPosition(u, e.target.value)}
                            disabled={savingRow === `${u.id}:position`}
                            data-testid={`team-position-${u.id}`}
                            aria-label={`Position for ${u.name}`}
                            className="bg-[#0b0d12] border border-[#2a3447] rounded px-2 py-1 text-xs outline-none focus:border-[color:var(--accent)] disabled:opacity-60 max-w-[160px]"
                          >
                            <option value="">— None —</option>
                            {positions.map((p) => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                          </select>
                        </div>
                      ) : (
                        <span className="text-slate-400 text-xs">{positionById[u.position_id]?.name || "—"}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell" data-testid={`team-manager-cell-${u.id}`}>
                      {isAdmin ? (
                        <div className="flex items-center gap-1.5">
                          <UserRound size={12} className="text-slate-500 shrink-0" />
                          <select
                            value={u.manager_id || ""}
                            onChange={(e) => reassignManager(u, e.target.value)}
                            disabled={savingRow === `${u.id}:manager`}
                            data-testid={`team-manager-${u.id}`}
                            aria-label={`Manager for ${u.name}`}
                            className="bg-[#0b0d12] border border-[#2a3447] rounded px-2 py-1 text-xs outline-none focus:border-[color:var(--accent)] disabled:opacity-60 max-w-[160px]"
                          >
                            <option value="">— None —</option>
                            {eligibleManagers.filter((m) => m.id !== u.id).map((m) => (
                              <option key={m.id} value={m.id}>{m.name}</option>
                            ))}
                          </select>
                        </div>
                      ) : (
                        <span className="text-slate-400 text-xs">{userNameById[u.manager_id] || "—"}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell" data-testid={`team-lastseen-${u.id}`}>
                      {(() => {
                        const info = relativeAgo(presence[u.id]?.last_seen);
                        return (
                          <span className={`text-xs inline-flex items-center gap-1.5 ${info.tone}`} title={presence[u.id]?.last_seen || "No heartbeat yet"}>
                            <span className={`w-1.5 h-1.5 rounded-full ${info.online ? "bg-[color:var(--accent)]" : info.stale ? "bg-[#ff3b5c]" : "bg-slate-500"}`} />
                            {info.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {deletable ? (
                        <button
                          onClick={() => removeOne(u)}
                          data-testid={`team-delete-${u.id}`}
                          title={`Delete ${u.name}`}
                          className="text-slate-500 hover:text-[#ff3b5c]"
                        >
                          <Trash2 size={15} />
                        </button>
                      ) : (
                        <span className="text-slate-600" title={u.id === user.id ? "You can't delete yourself" : "Only admins can delete other admins"}>
                          <ShieldCheck size={14} />
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={10} className="text-center py-10 text-slate-500 text-sm">
                  <UsersIcon size={24} className="mx-auto mb-2 opacity-40" /> No teammates match your filters.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
